/**
 * The one "more" control a row may carry: an ellipsis that opens a short menu of what can be done to
 * the thing the row is. A row keeps at most one visible control beside its status (Layout.tsx rule 5,
 * the design language's "fewer controls"); Edit, Download, Archive and Remove go here instead of a
 * button each. Items that do not apply are left out, not shown disabled, unless the reason matters.
 */

import { Ellipsis } from '@gravity-ui/icons';
import { Button, Dropdown, Label } from '@heroui/react';

export interface RowMenuItem {
  id: string;
  label: string;
  /** Destructive: drawn in the danger colour, and conventionally last. */
  danger?: boolean;
  disabled?: boolean;
}

/** An ellipsis button and its menu; `label` names the thing ("More for Jira"). Falsy items are skipped, so a call site can write `cond && {…}`. */
export function RowMenu({ label, items, onAction }: { label: string; items: Array<RowMenuItem | false | null | undefined>; onAction: (id: string) => void }) {
  const shown = items.filter((i): i is RowMenuItem => Boolean(i));
  if (!shown.length) return null;
  return (
    <Dropdown>
      <Button size="sm" variant="ghost" isIconOnly aria-label={label} className="text-muted">
        <Ellipsis className="size-4" />
      </Button>
      <Dropdown.Popover placement="bottom end" className="min-w-48">
        <Dropdown.Menu aria-label={label} disabledKeys={shown.filter((i) => i.disabled).map((i) => i.id)} onAction={(k) => onAction(String(k))}>
          {shown.map((i) => (
            <Dropdown.Item key={i.id} id={i.id} textValue={i.label} {...(i.danger ? { variant: 'danger' as const } : {})}>
              <Label>{i.label}</Label>
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown.Popover>
    </Dropdown>
  );
}
